/**
 * "⋯" overflow menu for a table row — keeps rarely used and destructive
 * actions (deactivate/revoke, delete) out of the always-visible button row.
 *
 * The popover is portalled to <body> with fixed positioning: the table sits
 * in a scroll container that would otherwise clip it on the last rows. It
 * closes on Esc (focus returns to the trigger), an outside click, scroll or
 * resize; arrow keys move between items.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import IconButton from './IconButton';
import { MoreIcon } from './icons';

export interface RowAction {
  label: string;
  onSelect: () => void;
  danger?: boolean;
}

const MENU_WIDTH = 190;

const RowActionsMenu: React.FC<{ label: string; actions: RowAction[] }> = ({ label, actions }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  // Place under the trigger, right-aligned; flip above when there's no room below.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const height = menuRef.current?.offsetHeight ?? actions.length * 36 + 8;
    const below = r.bottom + 4;
    const top = below + height > window.innerHeight - 8 ? Math.max(8, r.top - height - 4) : below;
    const left = Math.min(Math.max(8, r.right - MENU_WIDTH), window.innerWidth - MENU_WIDTH - 8);
    setPos({ top, left });
  }, [open, actions.length]);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus({ preventScroll: true });
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      close(false);
    };
    const onMove = () => close(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, close]);

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? []);
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close(true);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      items[(at + step + items.length) % items.length]?.focus({ preventScroll: true });
    } else if (e.key === 'Tab') {
      close(false);
    }
  };

  return (
    <>
      <IconButton
        ref={triggerRef}
        size="sm"
        label={`More actions for ${label}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <MoreIcon />
      </IconButton>
      {open &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={`More actions for ${label}`}
            onKeyDown={onMenuKeyDown}
            style={{
              position: 'fixed',
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              width: MENU_WIDTH,
              zIndex: 'var(--z-overlay)',
              background: 'var(--bg-surface-1)',
              border: '1px solid var(--border-1)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-lg)',
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              fontFamily: 'var(--font-sans)',
            }}
          >
            {actions.map((a) => (
              <button
                key={a.label}
                type="button"
                role="menuitem"
                onClick={() => {
                  close(false);
                  a.onSelect();
                }}
                onMouseEnter={(e) => e.currentTarget.focus({ preventScroll: true })}
                style={{
                  textAlign: 'left',
                  border: 'none',
                  background: 'transparent',
                  borderRadius: 'var(--radius-sm)',
                  padding: '8px 10px',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  color: a.danger ? 'var(--danger)' : 'var(--fg-1)',
                }}
                onFocus={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onBlur={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                {a.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};

export default RowActionsMenu;
